import type { ReactNode } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import { FAVES, BRANDS } from "../concept-content";
import { upcomingLives } from "@/lib/schedule";
import { EventCandle } from "./events/Candle";
import { FOLLOW, Signup } from "../ConceptBits";
import { CLUB, EASE, HEART, HOME, PAGES, Head, Ink, VIEW, datePart } from "./bits";
import "./SectionsB.css";

/* Favorites, Where I'll be, Brands, Loop, Footer. */

const SAG = [27, 40, 27];

export function Favorites() {
  return <section id="favorites" className="b2-sec b2-faves" aria-labelledby="b2-faves-title">
    <div className="b2-wrap">
      <div className="b2-row">
        <Head id="b2-faves-title" hand="stuff I" word="love" tone="pink" />
        <div className="b2-row-aside">
          <p className="b2-note">tools, machines, and fabric I actually use</p>
          <Link to={PAGES.favorites} className="b2-btn b2-btn-blue">See all my favorites <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </div>
      <div className="b2-line">
        <svg className="b2-line-string" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true"><path d="M-20 6 Q500 74 1020 6" /></svg>
        <ul>
          {FAVES.map((f, i) => { const r = [-4, 2, 5][i]; return <motion.li key={f.name} style={{ marginTop: SAG[i] }}
            initial={{ opacity: 0, y: -30, rotate: r * 2 }} whileInView={{ opacity: 1, y: 0, rotate: r }} whileHover={{ rotate: [r, r - 5, r + 3, r] }} viewport={VIEW}
            transition={{ delay: i * 0.12, type: "spring", stiffness: 120, damping: 10 }}>
            <i className="b2-pin" aria-hidden="true" />
            <Link to={PAGES.favorites} className="b2-fave">
              <span className="b2-fave-img"><img src={f.img} alt="" /></span>
              <span className="b2-fave-tag">{f.tag}</span>
              <b>{f.name}</b>
              <span className="b2-note">{f.why}</span>
            </Link>
          </motion.li>; })}
        </ul>
      </div>
      <p className="b2-disclose">Some links are affiliate links. If you buy through them, I may earn a small commission at no extra cost to you.</p>
    </div>
  </section>;
}

/** The first event ticket, kept for the event-card design page. */
export function EventTicket() {
  return <motion.div className="b2-ticket-wrap" initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -2 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
    <Link to="/events/stitches-night-out" className="b2-ticket">
      <span className="b2-ticket-img"><img src="/events/practical-magic-2.jpg" alt="" /></span>
      <span className="b2-ticket-body">
        <small>Admit one</small>
        <b>Stitches Night Out</b>
        <span className="b2-ticket-what">A sewists’ movie night</span>
        <span><MapPin size={15} aria-hidden="true" /> American Fork, UT</span>
      </span>
      <span className="b2-ticket-stub"><small>Oct</small><b>20</b><span>Tickets <ArrowUpRight size={14} aria-hidden="true" /></span></span>
    </Link>
  </motion.div>;
}

export function WhereIllBe({ event }: { event?: ReactNode }) {
  const [next] = upcomingLives(1);
  const club = next.kind === "club";
  return <section className="b2-sec b2-where" aria-labelledby="b2-where-title">
    <div className="b2-wrap">
      <div className="b2-row">
        <Head id="b2-where-title" hand="come say" word="hi!" tone="pink" />
        <div className="b2-row-aside">
          <p className="b2-note">I’d love to meet you in real life (or on a Tuesday night).</p>
        </div>
      </div>
      <div className="b2-where-grid">
        {event ?? <EventCandle />}
        <Link to={PAGES.retreats} className="b2-spot b2-spot-pink">
          <small>In person</small>
          <b>Sewing retreats</b>
          <span>A few days away to sew, eat, and laugh with me.</span>
          <em>See retreats <ArrowRight size={15} aria-hidden="true" /></em>
        </Link>
        <a href={next.url} target="_blank" rel="noreferrer" className="b2-spot b2-spot-yellow">
          <small>{club ? "Online · the club" : "Online · free on YouTube"}</small>
          <b>{club ? "Next club class" : "Next live"}</b>
          <span className="b2-spot-date">{datePart(next.start, { weekday: "long", month: "short", day: "numeric" })}
            <svg viewBox="0 0 240 70" preserveAspectRatio="none" aria-hidden="true"><Ink d="M180 8 C 110 -2, 14 8, 8 36 C 2 64, 120 70, 200 60 C 250 52, 244 18, 200 10" delay={0.6} width={2.6} /></svg>
          </span>
          <em>{club ? "Members, 7 PM MT" : "Watch live, 7 PM MT"} <ArrowRight size={15} aria-hidden="true" /></em>
        </a>
      </div>
    </div>
  </section>;
}

export function Brands() {
  return <section className="b2-brands" aria-labelledby="b2-brands-title">
    <h2 id="b2-brands-title" className="b2-note">brands &amp; friends I’ve made things with</h2>
    <ul>{BRANDS.map(b => <li key={b.name}><img src={b.src} alt={b.name} /></li>)}</ul>
    <Link to={PAGES.about} className="b2-brands-link">Work with me <ArrowRight size={15} aria-hidden="true" /></Link>
  </section>;
}

export function Loop() {
  return <section id="newsletter" className="b2-sec b2-loop" aria-labelledby="b2-loop-title">
    <div className="b2-wrap b2-loop-grid">
      <div>
        <Head id="b2-loop-title" hand="stay in the" word="loop" tone="blue" />
        <p className="b2-lede">One email a week. Mostly fabric. What I’m making, a helpful tip, and first dibs on retreats and events.</p>
        <Signup source="home-blue-2" className="b2-signup" button={<>Count me in <ArrowRight size={18} aria-hidden="true" /></>} done={<>You’re in! Watch your inbox <span aria-hidden="true">♡</span></>} />
        <p className="b2-loop-fine">No spam. Unsubscribe whenever.</p>
      </div>
      <div className="b2-env" aria-hidden="true">
        <div className="b2-env-back" />
        <motion.figure className="b2-env-letter" initial={{ y: 120, rotate: 0 }} whileInView={{ y: 0, rotate: -6 }} viewport={VIEW} transition={{ delay: 0.3, type: "spring", stiffness: 90, damping: 14 }}>
          <img src="/chelsea.JPG" alt="" />
        </motion.figure>
        <svg className="b2-env-front" viewBox="0 0 400 250" preserveAspectRatio="none"><path d="M0 70 L200 170 L400 70 V250 H0 Z" /><path d="M0 250 L170 150 M400 250 L230 150" /></svg>
        <p className="b2-env-addr">to: you<br />xo, chelsea <b>♥</b></p>
        <svg className="b2-env-heart" viewBox="0 0 30 46"><Ink d={HEART} delay={1} width={3.4} /></svg>
      </div>
    </div>
    <div className="b2-shelf" aria-hidden="true">
      <img className="b2-shelf-mug" src="/concept/mug.png" alt="" />
    </div>
  </section>;
}

const FOOTER = [
  { label: "About", href: PAGES.about },
  { label: "Videos", href: PAGES.videos },
  { label: "Loose Threads Club", href: CLUB },
  { label: "My favorites", href: PAGES.favorites },
  { label: "Retreats", href: PAGES.retreats },
  { label: "Shop", href: PAGES.shop },
  { label: "Links", href: PAGES.links },
];

export function Footer() {
  return <footer className="b2-foot">
    <div className="b2-wrap b2-foot-grid">
      <div>
        <Link to={HOME} className="b2-logo b2-foot-logo">A Few Loose Threads</Link>
        <p className="b2-note">I make colorful things (and I’d love for you to make them too).</p>
      </div>
      <nav aria-label="Footer"><ul>{FOOTER.map(l => <li key={l.href}><Link to={l.href}>{l.label}</Link></li>)}</ul></nav>
      <div>
        <p className="b2-follow-label">Follow along</p>
        <ul className="b2-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </div>
    </div>
    <p className="b2-foot-fine">© {new Date().getFullYear()} A Few Loose Threads · made with love and too much fabric</p>
  </footer>;
}
